import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  increment,
  setDoc,
  arrayUnion
} from 'firebase/firestore';
import { db } from './config';
import { paymentManagementService } from './paymentManagement';
import { getUserProfile } from './collections';

// Collection names
const COLLECTIONS = {
  REFERRALS: 'referrals',
  REFERRAL_EARNINGS: 'referralEarnings'
};

// Referral Service
export const referralService = {
  // Generate a unique referral code for a teacher
  async generateReferralCode(teacherUserId) {
    try {
      // Check if teacher already has a referral code
      const existingRef = doc(db, COLLECTIONS.REFERRALS, teacherUserId);
      const existingDoc = await getDoc(existingRef);
      
      if (existingDoc.exists()) {
        const data = existingDoc.data();
        return { 
          success: true, 
          referralCode: data.referralCode,
          referralLink: this.getReferralLink(data.referralCode)
        };
      }

      // Generate a unique referral code (6-8 characters, alphanumeric)
      let referralCode;
      let isUnique = false;
      let attempts = 0;
      const maxAttempts = 10;

      while (!isUnique && attempts < maxAttempts) {
        // Generate code: first 3 chars from userId + random 4 chars
        const userIdPrefix = teacherUserId.substring(0, 3).toUpperCase();
        const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();
        referralCode = `${userIdPrefix}${randomChars}`;

        // Check if code already exists
        const codeQuery = query(
          collection(db, COLLECTIONS.REFERRALS),
          where('referralCode', '==', referralCode)
        );
        const codeSnapshot = await getDocs(codeQuery);
        
        if (codeSnapshot.empty) {
          isUnique = true;
        }
        attempts++;
      }

      if (!isUnique) {
        // Fallback: use timestamp-based code
        referralCode = `REF${Date.now().toString(36).toUpperCase().substring(0, 5)}`;
      }

      // Create referral document
      await setDoc(existingRef, {
        teacherUserId,
        referralCode,
        totalReferrals: 0,
        totalEarnings: 0,
        referredUsers: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { 
        success: true, 
        referralCode,
        referralLink: this.getReferralLink(referralCode)
      };
    } catch (error) {
      console.error('Error generating referral code:', error);
      return { success: false, error: error.message };
    }
  },

  // Get referral code for a teacher
  async getReferralCode(teacherUserId) {
    try {
      const ref = doc(db, COLLECTIONS.REFERRALS, teacherUserId);
      const docSnap = await getDoc(ref);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { 
          success: true, 
          referralCode: data.referralCode,
          referralLink: this.getReferralLink(data.referralCode),
          totalReferrals: data.totalReferrals || 0,
          totalEarnings: data.totalEarnings || 0
        };
      } else {
        // Generate if doesn't exist
        return await this.generateReferralCode(teacherUserId);
      }
    } catch (error) {
      console.error('Error getting referral code:', error);
      return { success: false, error: error.message };
    }
  },

  // Get referral link
  getReferralLink(referralCode) {
    if (typeof window !== 'undefined') {
      const baseUrl = window.location.origin;
      return `${baseUrl}/register?ref=${referralCode}`;
    }
    // Fallback for server-side rendering
    return `https://mahstry.com/register?ref=${referralCode}`;
  },

  // Process referral when a new user registers
  async processReferral(newUserId, referralCode) {
    try {
      if (!referralCode) {
        return { success: false, error: 'No referral code provided' };
      }

      // Find the referrer by referral code
      const codeQuery = query(
        collection(db, COLLECTIONS.REFERRALS),
        where('referralCode', '==', referralCode.toUpperCase())
      );
      const codeSnapshot = await getDocs(codeQuery);

      if (codeSnapshot.empty) {
        return { success: false, error: 'Invalid referral code' };
      }

      const referrerDoc = codeSnapshot.docs[0];
      const referrerData = referrerDoc.data();
      const referrerUserId = referrerData.teacherUserId;

      // Prevent self-referral
      if (referrerUserId === newUserId) {
        return { success: false, error: 'Cannot use your own referral code' };
      }

      // Check if this user was already referred (prevent duplicate referrals)
      const existingReferralQuery = query(
        collection(db, COLLECTIONS.REFERRAL_EARNINGS),
        where('referredUserId', '==', newUserId)
      );
      const existingSnapshot = await getDocs(existingReferralQuery);
      
      if (!existingSnapshot.empty) {
        return { success: false, error: 'User already referred' };
      }

      // Update referral document
      const referralRef = doc(db, COLLECTIONS.REFERRALS, referrerUserId);
      await updateDoc(referralRef, {
        totalReferrals: increment(1),
        totalEarnings: increment(10),
        referredUsers: arrayUnion(newUserId),
        updatedAt: serverTimestamp()
      });

      // Create referral earnings record
      const referralEarningData = {
        referrerUserId,
        referredUserId: newUserId,
        referralCode,
        amount: 10,
        status: 'credited',
        createdAt: serverTimestamp()
      };
      const earningsDocRef = await addDoc(collection(db, COLLECTIONS.REFERRAL_EARNINGS), referralEarningData);
      console.log('Referral earnings record created:', earningsDocRef.id, referralEarningData);

      // Add referral earnings to teacher's payment record
      // This will be included in the total earnings calculation
      await this.addReferralEarningToTeacher(referrerUserId, 10);

      return { 
        success: true, 
        referrerUserId,
        amount: 10
      };
    } catch (error) {
      console.error('Error processing referral:', error);
      return { success: false, error: error.message };
    }
  },

  // Add referral earning to teacher's payment record
  async addReferralEarningToTeacher(teacherUserId, amount) {
    try {
      // Find the teacher's document ID
      const teacherQuery = query(
        collection(db, 'tuitionTeachers'),
        where('userId', '==', teacherUserId)
      );
      const teacherSnapshot = await getDocs(teacherQuery);

      if (teacherSnapshot.empty) {
        console.warn('Teacher not found for referral earning');
        return { success: false };
      }

      // Create a referral payment record similar to teacher hiring payments
      // This ensures it's included in earnings calculations
      const { COLLECTIONS: COLLECTIONS_MAIN } = await import('./collections');
      
      await addDoc(collection(db, COLLECTIONS_MAIN.TEACHER_HIRING_PAYMENTS), {
        teacherId: teacherSnapshot.docs[0].id,
        paymentAmount: amount,
        paymentStatus: 'completed',
        paymentType: 'referral_bonus',
        description: 'Referral bonus',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error adding referral earning:', error);
      return { success: false, error: error.message };
    }
  },

  // Get referral statistics for a teacher
  async getReferralStats(teacherUserId) {
    try {
      const ref = doc(db, COLLECTIONS.REFERRALS, teacherUserId);
      const docSnap = await getDoc(ref);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          success: true,
          referralCode: data.referralCode,
          referralLink: this.getReferralLink(data.referralCode),
          totalReferrals: data.totalReferrals || 0,
          totalEarnings: data.totalEarnings || 0
        };
      } else {
        // Generate if doesn't exist
        return await this.generateReferralCode(teacherUserId);
      }
    } catch (error) {
      console.error('Error getting referral stats:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all referrals for a teacher
  async getTeacherReferrals(teacherUserId) {
    try {
      console.log('Fetching referrals for teacher:', teacherUserId);
      
      // Try with orderBy first (requires index)
      let snapshot;
      try {
        const referralsQuery = query(
          collection(db, COLLECTIONS.REFERRAL_EARNINGS),
          where('referrerUserId', '==', teacherUserId),
          orderBy('createdAt', 'desc')
        );
        snapshot = await getDocs(referralsQuery);
        console.log('Query with orderBy succeeded, found', snapshot.size, 'referrals');
      } catch (orderByError) {
        // If orderBy fails (index not created), try without it
        console.warn('OrderBy query failed, trying without orderBy:', orderByError.message);
        const referralsQuery = query(
          collection(db, COLLECTIONS.REFERRAL_EARNINGS),
          where('referrerUserId', '==', teacherUserId)
        );
        snapshot = await getDocs(referralsQuery);
        console.log('Query without orderBy succeeded, found', snapshot.size, 'referrals');
      }
      
      const referrals = [];
      const userIds = new Set();
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Referral document:', doc.id, data);
        referrals.push({ 
          id: doc.id, 
          ...data,
          // Ensure we have a date for sorting
          createdAt: data.createdAt || { seconds: 0 }
        });
        
        // Collect unique user IDs to fetch names
        if (data.referredUserId) {
          userIds.add(data.referredUserId);
        }
      });

      // Fetch user names for all referred users
      const userNamesMap = new Map();
      const userFetchPromises = Array.from(userIds).map(async (userId) => {
        try {
          const userResult = await getUserProfile(userId);
          if (userResult.success && userResult.data) {
            const userName = userResult.data.name || 
                           userResult.data.displayName || 
                           userResult.data.email?.split('@')[0] || 
                           'Unknown User';
            userNamesMap.set(userId, userName);
          } else {
            userNamesMap.set(userId, 'Unknown User');
          }
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          userNamesMap.set(userId, 'Unknown User');
        }
      });

      // Wait for all user fetches to complete
      await Promise.all(userFetchPromises);

      // Add user names to referrals
      referrals.forEach((referral) => {
        if (referral.referredUserId) {
          referral.referredUserName = userNamesMap.get(referral.referredUserId) || 'Unknown User';
        }
      });

      // Sort in memory if orderBy wasn't used
      referrals.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });

      console.log('Returning referrals with names:', referrals);
      return { success: true, referrals };
    } catch (error) {
      console.error('Error getting teacher referrals:', error);
      return { success: false, error: error.message, referrals: [] };
    }
  }
};

